import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Alert, Button, TextField, InputAdornment, IconButton, Typography, CircularProgress } from '@mui/material';
import { LockOutlined, Visibility, VisibilityOff, CheckCircleOutline, ArrowBack } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import { resetPassword } from '../components/api/user';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #0f1720;
    background-image: url('/BG_Aitomotivelab_nologo.jpg');
    background-size: cover;
    background-position: center;
    filter: brightness(0.2);
    z-index: -1;
  }
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  border: 1px solid rgba(255, 255, 255, 0.18);

  @media (max-width: 600px) {
    padding: 30px;
    width: 95%;
  }
`;

const Logo = styled.img`
  width: 160px;
  filter: drop-shadow(0px 0px 10px rgba(255, 255, 255, 0.5));
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 20px;
  .MuiOutlinedInput-root {
    border-radius: 10px;
    background-color: rgba(255, 255, 255, 0.1);
    &:hover fieldset { border-color: #00a8e8; }
  }
  .MuiInputLabel-root, .MuiOutlinedInput-input { color: #ffffff; }
  .MuiOutlinedInput-notchedOutline { border-color: rgba(255, 255, 255, 0.3); }
`;

const SubmitButton = styled(Button)`
  background: linear-gradient(45deg, #00a8e8 30%, #00e4d0 90%);
  border-radius: 10px;
  color: white;
  height: 48px;
  padding: 0 30px;
  box-shadow: 0 3px 5px 2px rgba(0, 168, 232, .3);
  transition: all 0.3s ease-in-out;
  &:hover {
    background: linear-gradient(45deg, #00e4d0 30%, #00a8e8 90%);
    transform: translateY(-2px);
  }
`;

const ResetPasswordPage = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      setError('Compila tutti i campi');
      return;
    }
    if (newPassword.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await resetPassword(uid, token, newPassword);
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.error;
      setError(msg || 'Link non valido o scaduto. Richiedi un nuovo link di recupero.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Card
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Logo src="https://aitomotivelab.com/images/logo/AitomotiveLab_White.svg" alt="AitomotiveLab" />
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutline style={{ color: '#00e4d0', fontSize: 60, marginBottom: 16 }} />
            <Typography variant="h6" style={{ color: '#ffffff', marginBottom: 12 }}>
              Password reimpostata!
            </Typography>
            <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 28 }}>
              La tua password è stata aggiornata con successo. Puoi ora effettuare il login.
            </Typography>
            <SubmitButton fullWidth variant="contained" onClick={() => navigate('/login')}>
              Vai al Login
            </SubmitButton>
          </div>
        ) : (
          <>
            <Typography variant="h6" style={{ color: '#ffffff', marginBottom: 8, textAlign: 'center' }}>
              Nuova Password
            </Typography>
            <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.65)', marginBottom: 24, textAlign: 'center' }}>
              Scegli una nuova password sicura per il tuo account.
            </Typography>

            {error && (
              <Alert severity="error" style={{ marginBottom: 16, backgroundColor: 'rgba(211,47,47,0.15)', color: '#ffffff' }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <StyledTextField
                fullWidth
                label="Nuova Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                disabled={loading}
                helperText="Minimo 8 caratteri"
                FormHelperTextProps={{ style: { color: 'rgba(255,255,255,0.5)' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined style={{ color: '#ffffff' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" style={{ color: '#ffffff' }}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                fullWidth
                label="Conferma Password"
                type={showConfirm ? 'text' : 'password'}
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined style={{ color: '#ffffff' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" style={{ color: '#ffffff' }}>
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <SubmitButton fullWidth type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={22} style={{ color: '#fff' }} /> : 'Reimposta Password'}
              </SubmitButton>
            </form>

            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/forgot-password')}
                style={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontSize: 13 }}
              >
                Richiedi nuovo link
              </Button>
            </div>
          </>
        )}
      </Card>
    </PageContainer>
  );
};

export default ResetPasswordPage;
